<template>
    <div
        v-if="!loading"
        class="nibnut-data-table"
    >
        <div v-if="!!searchable||!!exportable||!!$slots.controls" class="data-table-controls">
            <slot name="controls"></slot>
            <slot name="mid-controls"></slot>
            <data-table-search-box
                v-if="searchable"
                :id="`${id}-searchbox`"
                v-model="query"
            />
            <div v-if="exportable">
                <base-button
                    flavor="link"
                    @click.prevent="preparing_export=!preparing_export"
                >
                    <open-icon glyph="cloud-download" title="Export" />
                </base-button>
            </div>
        </div>
        <div
            v-if="!loading && !total"
            class="uk-panel uk-panel-box"
        >
            <slot name="empty">
                <h3 class="uk-panel-title uk-text-center uk-text-muted">{{ empty_title }}</h3>
                <div v-if="canAdd" class="uk-text-center">
                    <base-button
                        color="primary"
                        @click.prevent="$emit('add')"
                    >
                        <open-icon
                            glyph="plus"
                            class="mr-2"
                        />
                        {{ empty_add_button_title }}
                    </base-button>
                </div>
            </slot>
        </div>
        <table v-else-if="!loading" :class="tableClass" class="uk-table">
            <thead v-if="showHead">
                <tr>
                    <th v-if="small_screen" class="menu-sort">
                        <form-select
                            :id="`${id}-sorts`"
                            name="sorts"
                            :value="current_menu_sort"
                            :options="menu_sorts"
                            :required="false"
                            @input="menu_sort"
                        />
                    </th>
                    <data-table-header-cell
                        v-else
                        v-for="(column, field) in columns"
                        :key="field"
                        :field="field"
                        :label="column.label || field"
                        :sort="column.sort"
                        :type="column.type"
                        :compact="compactHeaders"
                        @click="sort"
                        :class="column.head_class"
                    />
                </tr>
            </thead>
            <tbody ref="tbody">
                <tr v-if="loading">
                    <td
                        :colspan="nb_columns"
                        class="loader-container"
                    >
                        <loader size="sm" />
                    </td>
                </tr>
                <tr
                    v-for="row in rows"
                    :key="row.id"
                    @click.prevent="$emit('click', row)"
                    :class="row_classes(row)"
                    class="lam-hoverable"
                >
                    <td
                        v-if="small_screen"
                    >
                        <slot
                            name="summary"
                            :row="row"
                        >
                            {{ row }}
                        </slot>
                    </td>
                    <td
                        v-else
                        v-for="(column, field) in columns"
                        :key="field"
                        :class="cell_classes(row, field, column)"
                    >
                        <slot
                            name="tbody"
                            :row="row"
                            :field="field"
                        >
                            {{ row[field] }}
                        </slot>
                    </td>
                </tr>
            </tbody>
            <tfoot
                v-if="!loading && canAdd"
            >
                <tr>
                    <td
                        :colspan="nb_columns"
                        class="uk-text-center data-table-footer-add"
                    >
                        <base-button
                            flavor="link"
                            size="small"
                            :block="true"
                            @click.prevent="$emit('add')"
                        >
                            <open-icon
                                glyph="plus"
                                class="uk-margin-right"
                            />
                            {{ add_button_title }}
                        </base-button>
                    </td>
                </tr>
            </tfoot>
        </table>
        <div v-if="!loading && !!total && ((totalPages > 1) || showTotals)">
            <ul v-if="totalPages > 1" class="uk-pagination">
                <li
                    :class="{ 'uk-invisible': !has_prev_page }"
                >
                    <a
                        href="#"
                        @click.prevent="goto_page(1)"
                    >
                        <open-icon
                            glyph="angle-double-left"
                        />
                    </a>
                </li>
                <li
                    :class="{ 'uk-invisible': !has_prev_page }"
                >
                    <a
                        href="#"
                        @click.prevent="goto_page(currentPage - 1)"
                    >
                        <open-icon
                            glyph="angle-left"
                        />
                    </a>
                </li>
                <li
                    v-for="page in pages"
                    :key="page.number"
                    :class="{ 'uk-active': page.number === currentPage }"
                >
                    <a
                        v-if="page.delta"
                        href="#"
                        @click.prevent="goto_page(currentPage + page.delta)"
                    >
                        ...
                    </a>
                    <span
                        v-else-if="page.number === currentPage"
                    >
                        {{ page.number }}
                    </span>
                    <a
                        v-else
                        href="#"
                        @click.prevent="goto_page(page.number)"
                    >
                        {{ page.number }}
                    </a>
                </li>
                <li
                    :class="{ 'uk-invisible': !has_next_page }"
                >
                    <a
                        href="#"
                        @click.prevent="goto_page(currentPage + 1)"
                    >
                        <open-icon
                            glyph="angle-right"
                        />
                    </a>
                </li>
                <li
                    :class="{ 'uk-invisible': !has_next_page }"
                >
                    <a
                        href="#"
                        @click.prevent="goto_page(totalPages)"
                    >
                        <open-icon
                            glyph="angle-double-right"
                        />
                    </a>
                </li>
            </ul>
            <div
                v-if="showTotals"
                :class="{ 'uk-margin-top': (totalPages <= 1) }"
                class="uk-text-small uk-text-muted uk-text-center"
            >
                <span v-if="found !== total">{{ found_caption }} / </span>{{ total_caption }}
            </div>
        </div>

        <export-builder
            :show.sync="preparing_export"
            :entity="exportEntity"
            :fields="exportFields"
            :query="exportQuery"
        />
    </div>
</template>

<script type="text/javascript">
import debounce from "lodash/debounce"
import orderBy from "lodash/orderBy"

import ui_utilities from "../../mixins/UiUtilities"
import handles_active_row from "../../mixins/HandlesActiveRow"

import DataTableSearchBox from "./DataTableSearchBox"
import DataTableHeaderCell from "./DataTableHeaderCell"
import BaseButton from "../BaseButton"
import FormSelect from "../FormSelect"
import OpenIcon from "../OpenIcon"
import Loader from "../Loader"
import ExportBuilder from "../../dialogs/ExportBuilder"

export default {
    name: "DataTable",
    mixins: [ui_utilities, handles_active_row],
    components: {
        DataTableSearchBox,
        DataTableHeaderCell,
        BaseButton,
        FormSelect,
        Loader,
        OpenIcon,
        ExportBuilder
    },
    watch: {
        search: "update_query",
        query: "debounce_query",
        $route: "update_query"
    },
    methods: {
        update_query () {
            if(this.query !== this.search) this.query = this.search
        },
        debounce_query: debounce(function () {
            this.$emit("search", this.query)
        }, 500),
        sort (field, sort) {
            this.$emit("sort", field, sort)
        },
        menu_sort (id, field, option) {
            this.sort(field, option.sort)
        },
        goto_page (page) {
            this.$emit("page", page)
        },
        prepare_export () {
            if(this.exportable) this.preparing_export = true
        },
        row_classes (row) {
            const classes = this.rowClassProvider ? this.rowClassProvider(row) : {}
            classes.clickable = this.clickable
            return classes
        },
        cell_classes (row, field, column) {
            let classes = column.cell_class || {}
            if(typeof column.cell_class === "string") classes = { [column.cell_class]: true }
            if(this.cellClassProvider) classes = { ...classes, ...this.cellClassProvider(row, field, column) }
            return classes
        }
    },
    computed: {
        nb_columns () {
            return Object.keys(this.columns).length
        },
        current_menu_sort () {
            const field = Object.keys(this.columns).find(field => !!this.columns[field].sort)
            if(field) return `${field}.${this.columns[field].sort}`
            return null
        },
        menu_sorts () {
            const menu_sorts = []
            Object.keys(this.columns).forEach(field => {
                menu_sorts.push({ id: `${field}.asc`, sort: "asc", name: `${this.columns[field].label} (low to high)` })
                menu_sorts.push({ id: `${field}.desc`, sort: "desc", name: `${this.columns[field].label} (high to low)` })
            })
            return orderBy(menu_sorts, "name", "asc")
        },
        pages () {
            const pages = []

            const visible_around = Math.ceil((this.pageRange - 1) / 2)

            let from = this.currentPage - visible_around
            if(from <= 0) from = 1

            let to = from + (this.pageRange - 1)
            if(to > this.totalPages) {
                from -= (to - this.totalPages)
                if(from <= 0) from = 1
                to = this.totalPages
            }

            if((from - 1) >= 1) pages.push({ number: null, delta: -1 })

            for(let loop = from; loop <= to; loop++) pages.push({ number: loop })

            if((this.totalPages - to) >= 1) pages.push({ number: null, delta: 1 })

            return pages
        },
        has_prev_page () {
            return this.currentPage > 1
        },
        has_next_page () {
            return this.currentPage < this.totalPages
        },
        found_caption () {
            return this.$root.translate("{number} {singular_entity_name} found:::{number} {plural_entity_name} found", { number: this.found, singular_entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase(), plural_entity_name: this.$root.translate(this.entityName, {}, 2).toLowerCase() }, this.found)
        },
        total_caption () {
            return this.$root.translate("{number} {singular_entity_name}:::{number} {plural_entity_name}", { number: this.total, singular_entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase(), plural_entity_name: this.$root.translate(this.entityName, {}, 2).toLowerCase() }, this.total)
        },
        empty_title () {
            return this.emptyTitle || this.$root.translate("No {plural_entity_name} set up yet", { plural_entity_name: this.$root.translate(this.entityName, {}, 2).toLowerCase() })
        },
        empty_add_button_title () {
            return this.emptyAddButtonTitle || this.$root.translate("Add your first {entity_name}...", { entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase() })
        },
        add_button_title () {
            return this.addButtonTitle || this.$root.translate("Add a new {entity_name}...", { entity_name: this.$root.translate(this.entityName, {}, 1).toLowerCase() })
        },
        exportable () {
            return (!!this.exportFields && !!this.exportFields.length)
        }
    },
    props: {
        id: {
            type: String,
            required: true
        },
        entityName: {
            type: String,
            default: "record:::records"
        },
        loading: {
            type: Boolean,
            default: false
        },
        columns: {
            type: Object,
            required: true
        },
        rows: {
            type: Array,
            required: true
        },
        total: {
            type: Number,
            default: 0
        },
        found: {
            type: Number,
            default: 0
        },

        currentPage: {
            type: Number,
            default: 1
        },
        perPage: {
            type: Number,
            default: 10
        },
        totalPages: {
            type: Number,
            default: 1
        },
        pageRange: {
            type: Number,
            default: 3
        },

        searchable: {
            type: Boolean,
            default: true
        },
        canAdd: {
            type: Boolean,
            default: false
        },
        clickable: {
            type: Boolean,
            default: true
        },
        compactHeaders: {
            type: Boolean,
            default: false
        },

        search: {
            type: String,
            default: ""
        },
        showHead: {
            type: Boolean,
            default: true
        },
        showTotals: {
            type: Boolean,
            default: true
        },

        tableClass: {
            default: null
        },
        rowClassProvider: {
            type: Function,
            default () {
                return () => {
                    return {}
                }
            }
        },
        cellClassProvider: {
            type: Function,
            default () {
                return () => {
                    return {}
                }
            }
        },
        emptyTitle: {
            type: String,
            default: ""
        },
        emptyAddButtonTitle: {
            type: String,
            default: ""
        },
        addButtonTitle: {
            type: String,
            default: ""
        },

        exportEntity: {
            type: String,
            default: ""
        },
        exportQuery: {
            type: Object,
            default () {
                return {}
            }
        },
        exportFields: {
            type: Array, // array of field ids
            default () {
                return []
            }
        }
    },
    data () {
        return {
            query: this.search || "", // local version; "search" prop is parent's version, and the one that triggers actual search
            preparing_export: false
        }
    }
}
</script>

<style lang="less">
@import "@/assets/less/variables";

.nibnut-data-table {
    select,
    textarea,
    input:not([type]),
    input[type="text"],
    input[type="password"],
    input[type="datetime"],
    input[type="datetime-local"],
    input[type="date"],
    input[type="month"],
    input[type="time"],
    input[type="week"],
    input[type="number"],
    input[type="email"],
    input[type="url"],
    input[type="search"],
    input[type="tel"],
    input[type="color"] {
        &:disabled { border-color: transparent; }
    }
    .data-table-controls {
        display: flex;
        justify-content: space-between;

        & > div {
            //margin: 0 $layout-spacing;

            &:first-child {
                margin-left: 0;
            }
            &:last-child {
                margin-right: 0;
            }
        }

        .data-table-search-box {
            width: 100%;
        }
    }
    thead {
        th {
            white-space: nowrap;

            & > span, .lam-table-sort {
                display: inline-block;
            }

            .lam-table-sort {
                font-size: 75%;
                margin-left: 3px;
            }
            &.menu-sort {
                padding-left: 0;
                padding-right: 0;
            }
            &.wrapped {
                white-space: normal;
                vertical-align: top;
            }
        }
        &.lam-addl-info-header {
            & > th {
                background-color: @global-light-color;
                color: white;
                font-size: 0.8em;
                line-height: 16px;
                padding-top: 1px;
                padding-bottom: 1px;

                .uk-button {
                    line-height: 16px;
                    min-height: 16px;
                    font-size: 11px;
                }
            }
        }

        &:last-child > th {
            font-family: @base-heading-font-family;
            font-weight: 100;
            text-transform: uppercase;
            background-color: @global-titles-background;
            /*border-bottom: 1px solid @global-muted-color;*/
        }
    }
    tbody {
        tr {
            td.loader-container {
                //padding: $control-padding-y $control-padding-x;
                text-align: center;

                & > .loader {
                    margin: 0 auto;
                }
            }

            &:last-child {
                td { border-bottom: 0; }
            }
        }
    }
    tfoot {
        font-family: @base-heading-font-family;
        font-style: normal;
        font-size: inherit;
        font-weight: 100;
        text-transform: uppercase;
        border-top: 2px solid @global-lam-daylight;

        td.data-table-footer-add {
            border-bottom: 0;
            //padding: $unit-1 $unit-2;
        }
    }

    .pagination {
        justify-content: center;
    }
}
@media print {
    .nibnut-data-table {
        font-size: 0.8em;

        thead {
            th {
                white-space: normal;
            }
        }
    }
}
</style>
